import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import DashboardHeroArtwork from '../components/DashboardHeroArtwork';
import HeroArtworkPicker from '../components/HeroArtworkPicker';

import PhotoGallery from '../components/PhotoGallery';
import { SpeciesBioProfile } from '../components/bioprofile/BioProfile';
// Home's hub (components/hub): the two isolate pipelines, and the table each
// node opens in place. It replaced the four count cards, the bank charts and
// the quick links.
import IsolateHub from '../components/hub/IsolateHub';
import MyTasksPanel from '../components/pipeline/MyTasksPanel';
import { canSeeHub } from '../components/hub/stages';
import {
  LIVING_ARTWORK, readStoredArtwork, readStoredInterval, readStoredMode, readStoredSingle, storeArtwork,
  storeInterval, storeMode, storeSingle,
} from '../utils/heroArtwork';
import { can } from '../utils/permissions';
import { availableWorkspaces, canUseWorkspace, clearWorkspace, getWorkspace, setWorkspace } from '../utils/workspace';

// The living hero is the largest piece of the organic library (the colony
// simulator, director and canvas renderer) and only this page uses it, so it
// is loaded on demand rather than carried in the eager bundle every screen
// -- the sign-in screen included -- downloads. The artwork layer's soil band
// is already painted while it arrives.
const HeroMycelium = lazy(() => import('../components/organic/HeroMycelium.jsx'));
// Home's photographs (2026-10-18): only for someone who chose a photo mode.
const HeroSlideshow = lazy(() => import('../components/hero/HeroSlideshow.jsx'));

/* ── Search helpers ──────────────────────────────────────────────── */

/* One group of results, a labelled group of options in the listbox. */
function SearchGroup({ id, label, color, children }) {
  return (
    <div role="group" aria-labelledby={id}>
      <div id={id} role="presentation" style={{ padding: '6px 14px 4px', fontSize: 11, fontWeight: 700, color, letterSpacing: 0.5, textTransform: 'uppercase', background: 'var(--c-bg-subtle-4)', borderBottom: '1px solid var(--c-bg-muted)' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

/* One result: an option of the combobox's listbox. The pointer chooses it
 * on mousedown, before the input's blur can close the list; the keyboard
 * through the input (aria-activedescendant names the active one). */
function SearchRow({ id, active, left, right, tag, onChoose, onHover, tip, testId }) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      onMouseDown={(e) => { e.preventDefault(); onChoose(); }}
      onMouseEnter={onHover}
      title={tip}
      data-testid={testId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 14px', minHeight: 40, boxSizing: 'border-box', cursor: 'pointer', background: active ? 'var(--c-primary-soft)' : 'var(--c-surface)', borderBottom: '1px solid var(--c-bg-subtle)', fontSize: 13, outline: active ? '2px solid var(--c-primary)' : 'none', outlineOffset: -2 }}
    >
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{left}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {right && <span style={{ color: 'var(--c-text-faint-fg)', fontSize: 12 }}>{right}</span>}
        {tag && <span style={{ background: tag.color, color: 'var(--c-on-accent)', borderRadius: 4, fontSize: 10, padding: '1px 6px', fontWeight: 600 }}>{tag.label}</span>}
      </span>
    </div>
  );
}

/** How many isolates the dropdown lists: the best matches (sort=relevance). */
const ISOLATE_RESULTS = 8;

function Heb({ children }) {
  return <span style={{ marginInlineStart: 10, color: 'var(--c-text-muted-fg)', direction: 'rtl', display: 'inline-block' }}>{children}</span>;
}

/* ── Dashboard ───────────────────────────────────────────────────── */

/** A rank or trophic mode in the reader's words (as the taxonomy page says
 *  them); a value with no label -- FUNGuild's composites -- as written. */
const rankText = (rank, t) => t(`common:rank.${rank}`, { defaultValue: String(rank).replace(/_/g, ' ') });
const trophicText = (mode, t) => t(`taxonomy:trophicMode.${mode}`, { defaultValue: String(mode).replace(/_/g, ' ') });

export default function Dashboard() {
  const { t } = useTranslation(['dashboard', 'regions', 'taxonomy', 'workspace', 'nav']);
  const { t: tTheme, i18n } = useTranslation('theme');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Read from storage once, lazily: the hero is above the fold, so resolving
  // it after the first paint would show the default artwork for a frame and
  // then swap it under the reader.
  const [artwork, setArtwork]       = useState(readStoredArtwork);
  const chooseArtwork = (next) => { setArtwork(next); storeArtwork(next); };

  // What the hero shows (2026-10-18): the animation above, the default, or
  // the bank photographs curators chose for Home (routes/home_slides.py) as
  // a rotating gallery or a single image. Per browser, like the artwork. The
  // photographs are asked for only when a photo mode is chosen or the picker
  // opens, so the default Home makes no request it did not make before.
  // The public survey's accounts never see the bank's photographs (the route
  // refuses them), so their hero stays the animation.
  const photoModes = (user?.roles || []).some((role) => role !== 'public');
  const [storedMode, setHeroMode]   = useState(readStoredMode);
  const heroMode = photoModes ? storedMode : 'animation';
  const [slideSeconds, setSlideSeconds] = useState(readStoredInterval);
  const [singleId, setSingleId]     = useState(readStoredSingle);
  const [slides, setSlides]         = useState({ state: 'idle', list: [] });
  const chooseMode = (next) => { setHeroMode(next); storeMode(next); };
  const chooseInterval = (next) => { setSlideSeconds(next); storeInterval(next); };
  const chooseSingle = (next) => { setSingleId(next); storeSingle(next); };
  const slidesAsked = useRef(false);
  const loadSlides = useCallback(() => {
    if (slidesAsked.current) return;
    slidesAsked.current = true;
    setSlides({ state: 'loading', list: [] });
    api.get('/home/slides')
      .then((r) => setSlides({ state: 'ready', list: r.data?.slides || [] }))
      .catch(() => {
        slidesAsked.current = false;      // the next opening of the picker asks again
        setSlides({ state: 'failed', list: [] });
      });
  }, []);
  useEffect(() => { if (heroMode !== 'animation') loadSlides(); }, [heroMode, loadSlides]);
  const photoHero = heroMode !== 'animation' && slides.state === 'ready' && slides.list.length > 0;

  const [query, setQuery]           = useState('');
  // { isolates: [...], isolateTotal, species: [...], failed } for the query
  // searched last: isolateTotal is how many isolates match in all.
  const [results, setResults]       = useState(null);
  const [showDrop, setShowDrop]     = useState(false);
  const [searching, setSearching]   = useState(false);
  const [active, setActive]         = useState(-1);
  const [selectedSpecies, setSelectedSpecies]   = useState(null); // taxon_name string, or null
  const [speciesDetail, setSpeciesDetail]       = useState(null);
  const [loadingSpecies, setLoadingSpecies]     = useState(false);
  // An isolate chosen from the search: the hub brings its row into view.
  const [reveal, setReveal]         = useState({ id: null, n: 0 });

  const searchRef   = useRef(null);
  const debounceRef = useRef(null);
  const searchSeq   = useRef(0);
  const searchIsolates = canSeeHub(user);

  // Reaching Home means the reader is in the Lab workspace. Without a stored
  // choice, the hub's own URL edits -- closing a table rewrites /?view=lab to
  // a bare / -- would otherwise send them to the chooser mid-task
  // (landingRedirect asks on a bare / with nothing stored). A bare / with
  // nothing stored never renders Home for someone who has a choice to make:
  // it redirects first. Field's roles are a subset of Lab's, so this never
  // skips a chooser that would have been shown.
  useEffect(() => {
    if (user && !getWorkspace(user) && canUseWorkspace(user, 'lab')) setWorkspace('lab');
  }, [user]);

  // The way back to the chooser, beside the greeting, as the account menu
  // has it (Layout.js): the stored choice is cleared first, so the chooser
  // asks rather than bouncing straight back here. Only for someone who has
  // a choice to make.
  const canSwitch = availableWorkspaces(user).length > 1;
  const switchWorkspace = () => {
    clearWorkspace();
    navigate('/workspace');
  };

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  /* One search, two answers at once: the isolates that match anything about
   * them (GET /specimens/overview?q=&sort=relevance -- its codes, its lab
   * records' codes, its taxon in Latin or Hebrew, its place where shown) and
   * the species in the bank (/search). Only the latest search's answers are
   * shown. Someone the hub is not for gets the species alone. */
  const runSearch = (q) => {
    const mine = ++searchSeq.current;
    setSearching(true);
    const isolates = searchIsolates
      ? api.get('/specimens/overview', { params: { q, sort: 'relevance', limit: ISOLATE_RESULTS } })
        .then((r) => {
          const items = Array.isArray(r?.data?.items) ? r.data.items : [];
          return { items, total: Number(r?.data?.total) || items.length };
        })
        .catch(() => null)
      : Promise.resolve({ items: [], total: 0 });
    const species = api.get(`/search?query=${encodeURIComponent(q)}`)
      .then((r) => (Array.isArray(r?.data?.species) ? r.data.species : []))
      .catch(() => null);
    Promise.all([isolates, species]).then(([iso, sp]) => {
      if (mine !== searchSeq.current) return;
      setResults({ isolates: iso?.items || [], isolateTotal: iso?.total || 0, species: sp || [],
        failed: iso === null && sp === null });
      setActive(-1);
      setShowDrop(true);
    }).finally(() => { if (mine === searchSeq.current) setSearching(false); });
  };

  const handleQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      searchSeq.current += 1;
      setResults(null);
      setShowDrop(false);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(q.trim()), 280);
  };

  const isolateResults = results?.isolates || [];
  const isolateTotal = results?.isolateTotal || 0;
  // More isolates match than the list shows: its last isolate option opens
  // them all in the table below.
  const moreIsolates = isolateResults.length > 0 && isolateTotal > isolateResults.length;
  const speciesResults = results?.species || [];
  const totalResults = isolateResults.length + speciesResults.length;
  // Every option in the order the keyboard walks them: the isolates (and
  // "show all" after them), then the species.
  const options = [
    ...isolateResults.map((item) => ({ kind: 'isolate', key: item.specimen_id, item })),
    ...(moreIsolates ? [{ kind: 'allIsolates', key: 'all-isolates' }] : []),
    ...speciesResults.map((item) => ({ kind: 'species', key: item.taxon_name, item })),
  ];
  const optionId = (i) => `hero-search-option-${i}`;
  const speciesStart = isolateResults.length + (moreIsolates ? 1 : 0);

  const selectSpecies = (taxonName) => {
    setShowDrop(false);
    setSelectedSpecies(taxonName);
    setSpeciesDetail(null);
    setLoadingSpecies(true);
    api.get(`/bank/species?name=${encodeURIComponent(taxonName)}`)
      .then(r => setSpeciesDetail(r.data))
      .catch(() => setSpeciesDetail(null))
      .finally(() => setLoadingSpecies(false));
  };

  /* An isolate chosen: the hub's table searched the same way, best match
   * first, its row opened (open=) and brought into view (hl=, reveal). A
   * reader with no lab visibility lands on "My isolates", the hub's own
   * default for them, where it is too. */
  const selectIsolate = (item) => {
    setShowDrop(false);
    const params = new URLSearchParams({ q: query.trim(), sort: 'relevance', open: item.specimen_id,
      hl: item.specimen_id });
    navigate(`/?${params.toString()}`);
    setReveal((r) => ({ id: item.specimen_id, n: r.n + 1 }));
  };

  /* Every isolate the search matches: the hub's table searched the same way,
   * best match first, with no row opened -- its own default view, the same
   * rows the dropdown counted. */
  const showAllIsolates = () => {
    setShowDrop(false);
    setActive(-1);
    navigate(`/?${new URLSearchParams({ q: query.trim(), sort: 'relevance' }).toString()}`);
  };

  const choose = (option) => {
    if (!option) return;
    if (option.kind === 'isolate') selectIsolate(option.item);
    else if (option.kind === 'allIsolates') showAllIsolates();
    else selectSpecies(option.item.taxon_name);
  };

  // The combobox's keys: arrows walk the options (and open the list again),
  // Enter chooses the active one -- with none active, and isolates found, it
  // shows them all in the table -- Escape closes the list and then clears.
  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!options.length) return;
      e.preventDefault();
      if (!showDrop) { setShowDrop(true); return; }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i < 0 && step < 0 ? options.length - 1 : (i + step + options.length) % options.length));
    } else if (e.key === 'Enter') {
      if (showDrop && active >= 0 && options[active]) {
        e.preventDefault();
        choose(options[active]);
      } else if (isolateResults.length > 0 && query.trim().length >= 2) {
        e.preventDefault();
        showAllIsolates();
      }
    } else if (e.key === 'Escape') {
      if (showDrop) {
        e.preventDefault();
        setShowDrop(false);
        setActive(-1);
      } else if (query) {
        setQuery('');
        setResults(null);
      }
    }
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('greetingMorning');
    if (h < 17) return t('greetingAfternoon');
    return t('greetingEvening');
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>

      {/* ── Hero ── */}
      <div style={{
        position: 'relative',
        background: 'linear-gradient(135deg, var(--c-primary-deep) 0%, var(--c-primary-4) 55%, var(--c-primary-5) 100%)',
        borderRadius: 18,
        padding: '36px 40px 40px',
        marginBottom: 24,
        minHeight: 180,
      }}>
        {/* The bank's photographs, when a photo mode is chosen and there are
            some: under the artwork layer, whose plain variant keeps the
            watermark over them. Otherwise the animation, as always. */}
        {photoHero && (
          <Suspense fallback={null}>
            <HeroSlideshow slides={slides.list} mode={heroMode} interval={slideSeconds} singleId={singleId}
              lang={i18n?.language === 'he' ? 'he' : 'en'} pauseLabel={tTheme('display.pause')}
              playLabel={tTheme('display.play')} photoAlt={tTheme('display.photoAlt')} />
          </Suspense>
        )}
        <DashboardHeroArtwork variant={photoHero ? 'none' : artwork} />
        {/* The living mycelium: a canvas colony above the artwork layer's soil
            band and watermark, below the greeting (zIndex 0 vs 1). It plays a
            bounded cycle, then settles with no work left running; reduced
            motion, Save-Data or its own pause button show the finished still.
            Mounted only while chosen, so picking another artwork stops it. */}
        {!photoHero && artwork === LIVING_ARTWORK && (
          <Suspense fallback={null}>
            <HeroMycelium pauseLabel={tTheme('artwork.pause')} playLabel={tTheme('artwork.play')} />
          </Suspense>
        )}
        <HeroArtworkPicker value={artwork} onChange={chooseArtwork}
          mode={photoModes ? heroMode : undefined} onModeChange={chooseMode} interval={slideSeconds} onIntervalChange={chooseInterval}
          slides={slides.list} slidesState={slides.state} singleId={singleId} onSingleChange={chooseSingle}
          onOpen={photoModes ? loadSlides : undefined} onManage={can(user, 'home:curate') ? () => navigate('/admin/home-slides') : undefined} />

        {/* Greeting, and the workspace switch beside it (wrapping under it
            on a narrow screen) */}
        <div className="hero-greeting">
          <h1 style={{ color: 'var(--c-on-accent)', margin: 0, fontSize: 30, fontWeight: 700 }}>
            {greeting()}, {user?.username}
          </h1>
          {canSwitch && (
            <button type="button" className="hero-switch" onClick={switchWorkspace}
              title={t('nav:tip.switchWorkspace')} data-testid="hero-switch-workspace">
              {t('workspace:switcher.change')}
            </button>
          )}
        </div>
        <p style={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,0.72)', margin: '0 0 30px', fontSize: 15 }}>
          {t('subtitle')}
        </p>

        {/* Search: a combobox over the isolates and the species */}
        <div ref={searchRef} style={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="search"
              role="combobox"
              aria-label={t('searchLabel')}
              aria-autocomplete="list"
              aria-expanded={!!(showDrop && results)}
              aria-controls="hero-search-results"
              aria-activedescendant={showDrop && active >= 0 ? optionId(active) : undefined}
              autoComplete="off"
              value={query}
              onChange={handleQueryChange}
              onKeyDown={onSearchKey}
              onFocus={() => results && totalResults > 0 && setShowDrop(true)}
              placeholder={t('searchPlaceholder')}
              title={t('tip.search')}
              data-testid="hero-search"
              style={{
                width: '100%',
                paddingBlock: 13,
                paddingInlineStart: 18,
                paddingInlineEnd: 44,
                fontSize: 15,
                borderRadius: showDrop && totalResults > 0 ? '9px 9px 0 0' : 9,
                border: 'none',
                outline: 'none',
                boxSizing: 'border-box',
                boxShadow: '0 2px 16px rgba(0,0,0,0.3)',
              }}
            />
            {searching && (
              <span style={{ position: 'absolute', insetInlineEnd: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-faint-fg)', fontSize: 13 }}>…</span>
            )}
          </div>

          {showDrop && results && totalResults > 0 && (
            <div id="hero-search-results" role="listbox" aria-label={t('searchLabel')}
              data-testid="hero-search-results" style={{
              position: 'absolute',
              top: '100%',
              insetInline: 0,
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border)',
              borderTop: 'none',
              borderRadius: '0 0 9px 9px',
              zIndex: 200,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              maxHeight: 420,
              overflowY: 'auto',
            }}>

              {/* Isolates: the best matches, each opening its row below */}
              {isolateResults.length > 0 && (
                <SearchGroup id="hero-search-isolates" label={t('groupIsolates')} color="var(--c-info-fg)">
                  {isolateResults.map((item, i) => (
                    <SearchRow key={item.specimen_id} id={optionId(i)} active={active === i}
                      onChoose={() => selectIsolate(item)} onHover={() => setActive(i)}
                      tip={t('tip.isolateResult')} testId={`hero-isolate-${item.specimen_id}`}
                      left={(
                        <>
                          <bdi dir="ltr" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 12 }}>
                            {item.specimen_code}
                          </bdi>
                          {' · '}
                          {item.taxon_name
                            ? <em dir="ltr" style={{ display: 'inline-block' }}>{item.taxon_name}</em>
                            : <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('isolateNoTaxon')}</span>}
                        </>
                      )}
                      right={item.lab_code ? <bdi dir="ltr">{item.lab_code}</bdi> : null}
                    />
                  ))}
                  {moreIsolates && (
                    <SearchRow id={optionId(isolateResults.length)} active={active === isolateResults.length}
                      onChoose={showAllIsolates} onHover={() => setActive(isolateResults.length)}
                      tip={t('tip.showAllIsolates')} testId="hero-isolates-all"
                      left={<span style={{ color: 'var(--c-info-fg)', fontWeight: 600 }}>
                        {t('showAllIsolates', { count: isolateTotal })}
                      </span>}
                    />
                  )}
                </SearchGroup>
              )}

              {/* Species in the bank, each opening its summary */}
              {speciesResults.length > 0 && (
                <SearchGroup id="hero-search-species" label={t('groupSpecies')} color="var(--c-primary)">
                  {speciesResults.map((item, j) => {
                    const i = speciesStart + j;
                    return (
                      <SearchRow key={item.taxon_name} id={optionId(i)} active={active === i}
                        onChoose={() => selectSpecies(item.taxon_name)} onHover={() => setActive(i)}
                        tip={t('tip.searchResult')} testId={`hero-species-${j}`}
                        left={<><em dir="ltr" style={{ display: 'inline-block' }}>{item.taxon_name}</em>{item.hebrew_name && <Heb>{item.hebrew_name}</Heb>}</>}
                        right={t('recordCount', { count: item.record_count })}
                        tag={{ label: rankText(item.taxonomic_rank || 'species', t), color: 'var(--c-primary-fg)' }}
                      />
                    );
                  })}
                </SearchGroup>
              )}

            </div>
          )}

          {showDrop && results && totalResults === 0 && (
            <div role="status" data-testid="hero-search-empty" style={{
              position: 'absolute', top: '100%', insetInline: 0,
              background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderTop: 'none',
              borderRadius: '0 0 9px 9px', padding: '14px 18px',
              fontSize: 14, color: 'var(--c-text-muted-fg)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}>
              {results.failed ? t('searchFailed') : t('noResults', { query })}
            </div>
          )}
        </div>
      </div>

      {/* ── Species Panel ── */}
      {selectedSpecies && (
        <div style={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 12, padding: '24px 28px', marginBottom: 32, position: 'relative' }}>
          <button onClick={() => setSelectedSpecies(null)} title={t('tip.closeSpecies')}
            style={{ position: 'absolute', insetInlineEnd: 18, top: 18, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' }}>×</button>
          {loadingSpecies && <p style={{ color: 'var(--c-text-faint-fg)' }}>{t('common:loading')}</p>}
          {!loadingSpecies && speciesDetail && (
            <>
              <h3 style={{ margin: '0 0 4px' }}>
                <em dir="ltr" style={{ fontStyle: 'italic' }}>{speciesDetail.taxon_name}</em>
                {speciesDetail.hebrew_name && <Heb>{speciesDetail.hebrew_name}</Heb>}
              </h3>
              <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                {[speciesDetail.taxonomic_rank && rankText(speciesDetail.taxonomic_rank, t),
                  speciesDetail.trophic_mode && trophicText(speciesDetail.trophic_mode, t)].filter(Boolean).join(' · ')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16, marginBottom: 18 }}>
                <div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-primary-fg)' }}>{speciesDetail.collection_count}</div><div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('panelCollections')}</div></div>
                <div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-info-fg)' }}>{speciesDetail.plate_count}</div><div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('panelPlates')}</div></div>
                <div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-purple-2-fg)' }}>{speciesDetail.active_slant_count}</div><div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('panelActiveSlants')}</div></div>
                <div><div style={{ fontSize: 22, fontWeight: 700, color: 'var(--c-purple-fg)' }}>{speciesDetail.dna_count}</div><div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{t('panelDna')}</div></div>
              </div>
              {speciesDetail.regions.length > 0 && (
                <p style={{ fontSize: 13, margin: '0 0 10px' }}>
                  <strong>{t('panelRegions')}:</strong> {speciesDetail.regions.map(r => t(r, { ns: 'regions' })).join(', ')}
                </p>
              )}
              {speciesDetail.culture_protocol && (
                <p style={{ fontSize: 13, margin: '0 0 10px' }}>
                  <strong>{t('panelProtocol')}:</strong> {speciesDetail.culture_protocol}
                </p>
              )}
              {speciesDetail.photos.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <PhotoGallery photos={speciesDetail.photos} thumbWidth={96} thumbHeight={72} testId="bank-species-photos" />
                </div>
              )}
              {/* The Species Catalog's scientific profile: all four buckets, reviewed here. */}
              <SpeciesBioProfile name={speciesDetail.taxon_name} tier="full" user={user}
                                 testId="bank-species-bioprofile" />
            </>
          )}
          {!loadingSpecies && !speciesDetail && <p style={{ color: 'var(--c-danger-fg)', fontSize: 13 }}>{t('panelLoadFailed')}</p>}
        </div>
      )}

      {/* ── My culture-pipeline tasks (nothing when there are none) ── */}
      <MyTasksPanel user={user} />

      {/* ── The hub: the isolate pipelines and the table each node opens ── */}
      <IsolateHub reveal={reveal} />
    </div>
  );
}
